import { useUnreadMessages } from '@zuruflex/api-client/react';
import {
  Avatar,
  AvatarFallback,
  LanguageSwitcher,
  Logo,
  ThemeToggle,
  UnreadDot,
} from '@zuruflex/ui';
import {
  BadgeCheck,
  CalendarDays,
  Car,
  FileText,
  Flag,
  ChartNoAxesColumn,
  ScrollText,
  Handshake,
  LayoutGrid,
  LogOut,
  Mail,
  MessageSquare,
  MessageSquareQuote,
  Settings,
  TicketPercent,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

interface NavItem {
  to: string;
  key: string;
  icon: typeof LayoutGrid;
  /** Only the index route needs exact matching. */
  end?: boolean;
  /** Hidden below MANAGER, where the page would only meet a 403. */
  managerUp?: boolean;
}

interface NavGroup {
  /** Omitted for the dashboard, which is home rather than a section. */
  key?: string;
  items: NavItem[];
}

/*
 * Grouped by the job someone opens the console to do: run today's rentals,
 * look after the people on either side of them, grow the marketplace, or
 * change how the platform itself behaves. Most-visited sections first.
 */
const NAV: NavGroup[] = [
  {
    items: [
      { to: '/', key: 'nav.dashboard', end: true, icon: LayoutGrid },
      { to: '/reports', key: 'nav.reports', icon: ChartNoAxesColumn },
    ],
  },
  {
    key: 'nav.group.rentals',
    items: [
      { to: '/bookings', key: 'nav.bookings', icon: CalendarDays },
      { to: '/fleet', key: 'nav.fleet', icon: Car },
      { to: '/inbox', key: 'nav.inbox', icon: MessageSquare },
    ],
  },
  {
    key: 'nav.group.people',
    items: [
      { to: '/customers', key: 'nav.customers', icon: Users },
      { to: '/partners', key: 'nav.partners', icon: Handshake },
    ],
  },
  {
    key: 'nav.group.growth',
    items: [
      { to: '/promotions', key: 'nav.promotions', icon: TicketPercent },
      { to: '/reviews', key: 'nav.reviews', icon: MessageSquareQuote },
      { to: '/badges', key: 'nav.badges', icon: BadgeCheck },
    ],
  },
  {
    key: 'nav.group.platform',
    items: [
      { to: '/settings', key: 'nav.settings', icon: Settings },
      { to: '/content', key: 'nav.content', icon: FileText, managerUp: true },
      { to: '/emails', key: 'nav.emails', icon: Mail },
      { to: '/flags', key: 'nav.flags', icon: Flag },
      { to: '/audit', key: 'nav.audit', icon: ScrollText, managerUp: true },
    ],
  },
];

/** "Ama Owusu" → "AO"; a single name still gives one letter. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * A black sidebar against a white canvas, curved along the edge where the
 * two meet so the chrome carries the same soft shape language as the cards
 * inside it — a panel radius one step above the card radius, and pills for
 * every nav row.
 *
 * The active nav item is the only yellow element in the chrome, so it is
 * never ambiguous where you are.
 *
 * Content runs the full width of the viewport — an operations table has
 * more columns than a reading measure, and centring it would waste the
 * screen.
 */
export function AppLayout() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const { user, signOut } = useAuth();
  const isManagerUp = user?.role === 'OWNER' || user?.role === 'MANAGER';

  /*
   * The one number in the chrome. A minute's poll rather than a stream —
   * nobody is staring at a badge, and a socket each for a count would be a
   * connection held open for information nobody is waiting on.
   */
  const unread = useUnreadMessages(api);

  return (
    <div className="flex min-h-screen bg-surface">
      {/*
        The sidebar is a black band in either theme, so it carries `dark`
        itself: everything nested in it — the switchers, the sign-out row —
        then resolves against dark tokens no matter what the page is set to.

        It runs to the top, bottom and left of the glass and curves only on
        the edge that faces the work surface — the one edge where the radius
        has a surface to be read against.
      */}
      <aside className="dark sticky top-0 hidden h-screen w-60 shrink-0 flex-col gap-2 overflow-y-auto rounded-r-panel bg-ink-950 p-3 md:flex">
        <div className="shrink-0 px-3 pb-2 pt-4">
          <Logo className="h-7" />
          <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted">
            {t('nav.section')}
          </p>
        </div>

        <nav className="flex flex-1 flex-col gap-5">
          {NAV.map((group) => ({
            ...group,
            items: group.items.filter((item) => !item.managerUp || isManagerUp),
          }))
            .filter((group) => group.items.length > 0)
            .map((group, index) => (
              <div key={group.key ?? index} className="flex flex-col gap-1">
                {group.key ? (
                  <p className="px-4 pb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted">
                    {t(group.key)}
                  </p>
                ) : null}
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      [
                        'flex items-center gap-3 rounded-pill px-4 py-2.5 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-accent text-accent-text'
                          : 'text-text-muted hover:bg-surface-hover hover:text-text',
                      ].join(' ')
                    }
                  >
                    <item.icon className="size-4" />
                    <span className="flex-1">{t(item.key)}</span>
                    {/*
                      Only on the inbox: the count is of unread messages, and a
                      dot on any other row would be a number about somewhere else.
                    */}
                    {item.to === '/inbox' && (unread.data ?? 0) > 0 ? (
                      <UnreadDot
                        count={unread.data ?? 0}
                        label={t('common:messaging.unread', { count: unread.data ?? 0 })}
                      />
                    ) : null}
                  </NavLink>
                ))}
              </div>
            ))}
        </nav>

        <div className="shrink-0 space-y-1">
          <LanguageSwitcher
            value={i18n.language}
            onChange={(locale) => void i18n.changeLanguage(locale)}
            label={t('common:field.language')}
            className="w-full justify-start px-4 text-text-muted hover:bg-surface-hover hover:text-text"
          />
          <ThemeToggle
            variant="full"
            className="w-full justify-start px-4 text-text-muted hover:bg-surface-hover hover:text-text"
          />
          <button
            type="button"
            onClick={() => void signOut()}
            className="group flex w-full items-center gap-3 rounded-pill p-2 pr-4 text-left transition-colors hover:bg-surface-hover"
          >
            <Avatar className="size-9">
              <AvatarFallback>{initials(user?.fullName ?? 'Zuruflex')}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-text">
                {user?.fullName ?? t('user.role')}
              </p>
              <p className="truncate text-[11px] text-text-muted">{user?.email}</p>
            </div>
            <LogOut
              className="size-4 shrink-0 text-text-muted group-hover:text-text"
              aria-label={t('common:action.signOut')}
            />
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border-default bg-surface px-4 py-3 md:hidden">
          <Logo className="h-6" />
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <LanguageSwitcher
              value={i18n.language}
              onChange={(locale) => void i18n.changeLanguage(locale)}
              variant="compact"
              label={t('common:field.language')}
            />
          </div>
        </header>

        <main className="flex w-full min-w-0 flex-1 flex-col px-4 py-6 md:px-6 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
